import { observer } from "mobx-react";
import moment from "moment";
import React, { useState, useCallback, useEffect, ReactElement } from "react";
import { Container, Row, Col, Modal, ListGroup } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { formatNumberWithCommas } from "../../../constants/common-constant";
import { ColumnType } from "../../../constants/enums/gridColumnTypeEnum";
import {
  initialPageLimit,
  PageLimitOptions,
} from "../../../constants/pageLimit.constant";
import { useStore } from "../../../contexts/StoreProvider";
import useWindowDimensions, {
  getTableMaxHeight,
} from "../../../helpers/windowResize.helper";
import { IActionFormatter } from "../../../models/IFormatter";
import IPageInfo from "../../../models/IPageInfo";
import { ISorting } from "../../../models/ISorting";
import { IClient } from "../../../models/response/IClientResponse";
import { IPayment } from "../../../models/response/IPaymentResponse";
import Grid, { getColumns } from "../../../shared-components/Grid/Grid";
import SpinnerLoader from "../../../shared-components/Loader/SpinnerLoader";
import DeletePopup from "../../../shared-components/Popup/DeletePopup";
import { formatMessage } from "../../../translations/formatMessage";
import NoData from "../../../assets/Images/no_record.png";
import Pagination from "../../../shared-components/Grid/Pagination";
import DraggableModal from "../../../shared-components/Draggable/DraggableModal";
import DeletePopupHOC from "../../../shared-components/Popup/Hoc/DeletePopupHoc";
import vehicleStore from "../../../core/stores/vehicle-store";
import {
  IFailedAssetsList,
  IGetFailedAssetsList,
} from "../../../models/response/IVehicleResponse";

interface IProps {
  showDeletePopup: boolean;
  modalClosed: () => void;
  deleteToggleHandler: () => void;
}

const FailedAssets: React.FC<IProps> = (props: IProps) => {
  const { paymentStore, preferencesStore } = useStore();
  const { showDeletePopup, deleteToggleHandler, modalClosed } = props;

  const {
    getFailedAssetsList,
    GetFailedAssetsListService,
    resetFailedAssetsState,
    FailedAssetsState,
    deleteFailedAssetsState,
    DeleteFailedAssetsService,
    resetDeleteFailedAssetsState,
  } = vehicleStore;
  const [data, setData] = useState<IFailedAssetsList[]>(getFailedAssetsList);
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
  });
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const onModalClosed = useCallback(() => {
    modalClosed();
    resetFailedAssetsState();
  }, [modalClosed, resetFailedAssetsState]);
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const [sorting, setSorting] = useState<ISorting>({
    column: "asset_id",
    orderBy: "asc",
  });
  const callGetPaymentList = useCallback(() => {
    setDisablePagination(true);
    GetFailedAssetsListService(
      pageInfo.currentPage,
      pageLimit,
      sorting.orderBy,
      sorting.column
    );
    setIsApiCalled(true);
  }, [
    pageInfo.currentPage,
    pageLimit,
    sorting.orderBy,
    sorting.column,
    GetFailedAssetsListService,
  ]);

  useEffect(() => {
    callGetPaymentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetPaymentList]);

  useEffect(() => {
    if (deleteFailedAssetsState.success) {
      setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: 1 }));
      callGetPaymentList();
      resetDeleteFailedAssetsState();
      deleteToggleHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetPaymentList, deleteFailedAssetsState.success]);

  useEffect(() => {
    setData(() => getFailedAssetsList);
  }, [getFailedAssetsList, setData]);
  // const [filterData, setFilterData] = useState<any>({...filter});
  const { height, width } = useWindowDimensions();

  const deleteClickHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    deleteToggleHandler();
  };
  const addIcons: Array<IActionFormatter<ReactElement>> = [
    {
      handler: deleteClickHandler,
      icon: (
        <svg
          version="1.1"
          x="0px"
          y="0px"
          width="13"
          height="13"
          viewBox="0 0 512 512"
        >
          <g>
            <g>
              <path
                d="M62.205,150l26.569,320.735C90.678,493.865,110.38,512,133.598,512h244.805c23.218,0,42.92-18.135,44.824-41.265
    L449.795,150H62.205z M180.986,452c-7.852,0-14.458-6.108-14.956-14.063l-15-242c-0.513-8.276,5.771-15.395,14.033-15.908
    c8.569-0.601,15.381,5.757,15.908,14.033l15,242C196.502,444.632,189.721,452,180.986,452z M271,437c0,8.291-6.709,15-15,15
    c-8.291,0-15-6.709-15-15V195c0-8.291,6.709-15,15-15s15,6.709,15,15V437z M360.97,195.938l-15,242
    c-0.493,7.874-7.056,14.436-15.908,14.033c-8.262-0.513-14.546-7.632-14.033-15.908l15-242
    c0.513-8.276,7.764-14.297,15.908-14.033C355.199,180.543,361.483,187.662,360.97,195.938z"
              />
            </g>
          </g>
          <g>
            <g>
              <path
                d="M451,60h-90V45c0-24.814-20.186-45-45-45H196c-24.814,0-45,20.186-45,45v15H61c-16.569,0-30,13.431-30,30
    c0,16.567,13.431,30,30,30c137.966,0,252.039,0,390,0c16.569,0,30-13.433,30-30C481,73.431,467.569,60,451,60z M331,60H181V45
    c0-8.276,6.724-15,15-15h120c8.276,0,15,6.724,15,15V60z"
              />
            </g>
          </g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
          <g></g>
        </svg>
      ),
      // key: "IsEditable",
      key: "",
      title: "label_delete",
    },
  ];

  const columns = getColumns<any, number>([
    {
      columnType: ColumnType.Hidden,
      accessorKey: "Id",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "AssetId",
      displayName: "asset_id",
      // sortingName: "asset_id",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "RegistrationNo",
      displayName: "Registration Number",
      // sortingName: "registration_no",
    },
    // {
    //   columnType: ColumnType.ActionsFormatter,
    //   icons: addIcons,
    //   displayName: "label_action",
    // },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "TryCount",
      displayName: "Try Count",
      // sortingName: "try_count",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "RequestDate",
      displayName: "Request Date",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "Tid",
      displayName: "Transaction ID (Tid)",
      // sortingName: "tid",
    },
  ]);

  const deleteHandler = () => {
    DeleteFailedAssetsService(selectedRows);
  };

  const openDeleteClickHandler = () => {
    deleteToggleHandler();
  };

  return (
    <React.Fragment>
      {FailedAssetsState.inProgress && <SpinnerLoader />}
      {showDeletePopup && (
        <DeletePopup
          modalTitle="label_delete"
          modalSubmit={deleteHandler}
          modalClosed={() => {
            deleteToggleHandler();
            resetDeleteFailedAssetsState();
          }}
          isLoading={deleteFailedAssetsState.inProgress}
          message="delete_failed_assets_confirm"
          submitBtnText={"label_delete"}
          cancelBtnText={"button_cancel"}
        />
      )}
      <Modal
        className="add-failed-assets-modal add-user-modal "
        show={true}
        centered
        size="lg"
        dialogAs={DraggableModal}
      >
        <Modal.Header>
          <Modal.Title className="fs-5 ps-3">
            <FormattedMessage id="Failed Assets Allocation" />
          </Modal.Title>
          <div className=" d-flex justify-content-end">
            <ListGroup.Item
              className="border-0 bg-transparent p-0 me-2"
              onClick={() => {
                openDeleteClickHandler();
              }}
              disabled={!(selectedRows.length > 0)}
              title="Filter"
            >
              <div className="filter modal-filter-icon">
                <div className="btn btn-lg bg-white d-flex align-items-center gap-2 delete-failed">
                  <svg
                    version="1.1"
                    x="0px"
                    y="0px"
                    width="16"
                    height="16"
                    viewBox="0 0 512 512"
                  >
                    <g>
                      <g>
                        <path
                          d="M62.205,150l26.569,320.735C90.678,493.865,110.38,512,133.598,512h244.805c23.218,0,42.92-18.135,44.824-41.265
    L449.795,150H62.205z M180.986,452c-7.852,0-14.458-6.108-14.956-14.063l-15-242c-0.513-8.276,5.771-15.395,14.033-15.908
    c8.569-0.601,15.381,5.757,15.908,14.033l15,242C196.502,444.632,189.721,452,180.986,452z M271,437c0,8.291-6.709,15-15,15
    c-8.291,0-15-6.709-15-15V195c0-8.291,6.709-15,15-15s15,6.709,15,15V437z M360.97,195.938l-15,242
    c-0.493,7.874-7.056,14.436-15.908,14.033c-8.262-0.513-14.546-7.632-14.033-15.908l15-242
    c0.513-8.276,7.764-14.297,15.908-14.033C355.199,180.543,361.483,187.662,360.97,195.938z"
                        />
                      </g>
                    </g>
                    <g>
                      <g>
                        <path
                          d="M451,60h-90V45c0-24.814-20.186-45-45-45H196c-24.814,0-45,20.186-45,45v15H61c-16.569,0-30,13.431-30,30
    c0,16.567,13.431,30,30,30c137.966,0,252.039,0,390,0c16.569,0,30-13.433,30-30C481,73.431,467.569,60,451,60z M331,60H181V45
    c0-8.276,6.724-15,15-15h120c8.276,0,15,6.724,15,15V60z"
                        />
                      </g>
                    </g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                    <g></g>
                  </svg>
                  Delete
                </div>
              </div>
            </ListGroup.Item>
            <div onClick={onModalClosed} className="icons8-close">
              <span className="material-symbols-outlined fs-5 text-dark">
                close
              </span>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {" "}
          <Container fluid className="p-0 mb-3">
            {data.length !== 0 ? (
              <div>
                <div className="pt-3 px-3">
                  <div className="rounded border">
                    <div className="Fuel-table invoice-table">
                      <div
                        className="table-responsive"
                        style={getTableMaxHeight(height, width)}
                        data-testid="table"
                      >
                        {data && (
                          <Grid
                            data={data}
                            columns={columns}
                            isInProgress={FailedAssetsState.inProgress}
                            hiddenColumns={["Id"]}
                            rowSelectionEnabled={true}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            keyField="Tid"
                          />
                        )}
                      </div>
                    </div>
                    <Container fluid>
                      <Row className="tableFooter py-1 mt-0">
                        <Col xs="3" className="d-flex">
                          <b className="text-white text-hidden-m mt-1 pt-1">
                            <FormattedMessage id="show" />:{" "}
                          </b>
                          <select
                            data-testid="pageSelect"
                            className="ms-2 from-control"
                            onChange={onPageLimitChanged}
                            value={pageLimit}
                          >
                            {PageLimitOptions.map((op) => (
                              <option key={op} value={op}>
                                {op}
                              </option>
                            ))}
                          </select>
                        </Col>

                        <Col className="d-flex justify-content-end" xs="9">
                          <Pagination
                            totalPages={pageInfo.totalPages}
                            pageLimit={pageLimit}
                            onPageChanged={onPageChanged}
                            disablePagination={disablePagination}
                          />
                        </Col>
                      </Row>
                    </Container>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no_data_table border mx-2">
                <div className="no_data w-100 text-capitalize text-center">
                  <img src={NoData} alt="NOData" className="NOData" />
                  <h1 className="fs-4 fw-semi-bold">
                    <FormattedMessage id="message_no_datafound" />
                  </h1>
                </div>
              </div>
            )}
          </Container>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(FailedAssets));
